/**
 * SectionPanel — the titled form section every form groups its fields into
 * (BO UI Standard; user request 2026-09-23 "our UI should follow like this",
 * pointing at Sample Registration's "Sample Information" block).
 *
 * A bordered card with a light-blue header strip (icon + bold caption) and a
 * padded body:
 *
 *     ┌──────────────────────────────────────────┐
 *     │ 🧪  Sample Information                   │  ← .ai-sec-h
 *     ├──────────────────────────────────────────┤
 *     │  Label : field    Label : field          │  ← .ai-sec-b
 *     └──────────────────────────────────────────┘
 *
 * The visual source is Sample Registration's `.sr-sec` / `.sr-sec-h` /
 * `.sr-sec-b` (features/sampling/sampleRegistration/sampleRegistration.css).
 * That CSS is feature-local, so this component lifts the SAME tokens into a
 * shared place — reach for this from any form rather than copying `.sr-sec`
 * again (standing rule 8). Sample Registration itself still uses its own
 * classes; migrating it onto this component is a follow-up, not a behaviour
 * change, and was deliberately left out of the PLM work that created this.
 */
import { useState, type ReactNode } from 'react';
import './sectionPanel.css';

interface Props {
  /** Caption shown in the header strip. */
  title: string;
  /**
   * Collapsible info panel (BO UI Standard v4 §10): a chevron in the header
   * folds the body away so the working area below can take the height, and
   * clicking the header (or the chevron) restores it. Off by default — a panel
   * only collapses when the form says it may.
   */
  collapsible?: boolean;
  /** Start collapsed. Only meaningful with `collapsible`. */
  defaultCollapsed?: boolean;
  /** Optional leading glyph (emoji or a `ti-*` class name). */
  icon?: string;
  /** Optional muted note rendered after the caption. */
  note?: string;
  /** Extra class on the section root. */
  className?: string;
  /**
   * Controls rendered at the RIGHT end of the header strip (buttons, a count,
   * a small picker). They sit after the caption and any note, pushed right, so
   * a panel can carry its own actions instead of a separate bar above it.
   */
  headerRight?: ReactNode;
  /**
   * Rendered INSIDE the panel, ABOVE the header strip, flush with the panel's
   * top edge — a status bar that belongs to the section (e.g. PLM's Daily QC
   * bar, which the prototype docks on top of the Sequence card). Stays visible
   * when a collapsible panel is folded.
   */
  top?: ReactNode;
  children: ReactNode;
}

export function SectionPanel({
  title, icon, note, collapsible, defaultCollapsed, className, headerRight, top, children,
}: Props) {
  const [collapsed, setCollapsed] = useState(!!defaultCollapsed);
  const shut = collapsible && collapsed;

  const head = (
    <>
      {icon && (
        icon.startsWith('ti-')
          ? <i className={`ti ${icon} ai-sec-ico`} aria-hidden="true" />
          : <span className="ai-sec-ico" aria-hidden="true">{icon}</span>
      )}
      {title}
      {note && <span className="ai-sec-note">{note}</span>}
      {headerRight && (
        // Stop clicks reaching a collapsible header's toggle — a button in the
        // strip must run its own action, not fold the panel away under it.
        <span
          className="ai-sec-actions"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >{headerRight}</span>
      )}
    </>
  );

  return (
    <section
      className={`ai-sec${shut ? ' ai-sec--collapsed' : ''}${className ? ` ${className}` : ''}`}
    >
      {top}
      {collapsible ? (
        // The whole header is the control, so the hit target is the strip and
        // not just the chevron — and it stays ONE button, so a screen reader
        // announces a single expand/collapse rather than a heading plus a
        // mystery icon.
        <button
          type="button"
          className="ai-sec-h ai-sec-h--btn"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? `Show ${title}` : `Hide ${title}`}
        >
          {head}
          <i className={`ti ti-chevron-${collapsed ? 'down' : 'up'} ai-sec-chev`} aria-hidden="true" />
        </button>
      ) : (
        <h3 className="ai-sec-h">{head}</h3>
      )}
      {!shut && <div className="ai-sec-b">{children}</div>}
    </section>
  );
}
